import { useTranslation } from "@next-platform/client-common-core";
import RulebookPickerTab from "@next-platform/client-common-core/rulebook/picker/RulebookPickerTab";
import { RulebookSelectionSort } from "@next-platform/client-common-core/rulebook/picker/rulebookSelection";
import { Button, ButtonSize, ButtonVariant, DropDownMenu, type DropDownMenuItem, IconVariants } from "@next-platform/client-common-spicy-ui";
import React, { memo, useMemo } from "react";

/** The picker's tabs, as the Lorebook picker's My Lorebooks / Discover. */
type DropDownControlProps<T extends string> = {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  dataTestId: string;
};

const DropDownControl = <T extends string>({ value, options, onChange, dataTestId }: DropDownControlProps<T>) => {
  const listItems = useMemo<DropDownMenuItem[]>(
    () => options.map((option) => ({ label: option.label, value: option.value, onPress: () => onChange(option.value) })),
    [options, onChange],
  );
  const label = options.find((option) => option.value === value)?.label ?? options[0]?.label ?? "";
  return (
    <DropDownMenu listItems={listItems} placement="bottom-end">
      <div data-testid={`${dataTestId}-Root`}>
        <Button
          type="button"
          variant={ButtonVariant.OUTLINED}
          size={ButtonSize.MEDIUM}
          iconEnd={IconVariants.CHEVRON_DOWN}
          aria-label={label}
          data-testid={dataTestId}
        >
          {label}
        </Button>
      </div>
    </DropDownMenu>
  );
};

/** Latest / Oldest. */
export const RulebookSortControl = memo(
  ({ sort, onSort, dataTestId }: { sort: RulebookSelectionSort; onSort: (sort: RulebookSelectionSort) => void; dataTestId: string }) => {
    const { t } = useTranslation();
    const options = useMemo(
      () => [
        { value: RulebookSelectionSort.Latest, label: t("search:sortBy.latest") },
        { value: RulebookSelectionSort.Oldest, label: t("search:sortBy.oldest") },
      ],
      [t],
    );
    return <DropDownControl value={sort} options={options} onChange={onSort} dataTestId={`${dataTestId}-Sort`} />;
  },
);
RulebookSortControl.displayName = "RulebookSortControl";

/** My Rulebooks / Discover. */
export const RulebookTabControl = memo(
  ({ tab, onTab, dataTestId }: { tab: RulebookPickerTab; onTab: (tab: RulebookPickerTab) => void; dataTestId: string }) => {
    const { t } = useTranslation();
    const options = useMemo(
      () => [
        { value: RulebookPickerTab.Mine, label: t("rulebookEditor:attach.tab.mine", "My Rulebooks") },
        { value: RulebookPickerTab.Discover, label: t("rulebookEditor:attach.tab.discover", "Discover") },
      ],
      [t],
    );
    return <DropDownControl value={tab} options={options} onChange={onTab} dataTestId={`${dataTestId}-Tab`} />;
  },
);
RulebookTabControl.displayName = "RulebookTabControl";
